import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import { Container, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Button, InputGroup, Form, FloatingLabel, ListGroup } from "react-bootstrap";

import PhotoViewer from "../../common/photo/PhotoViewer";
import { BACKEND_URL } from "constants";

// Configuration map to handle the differences between Location and Username searches
const SEARCH_CONFIG = {
  location: {
    paramKey: "search_location",
    routeBase: "/find-user-location",
    searchEndpoint: "/find-location",
    photosEndpoint: "/photos/by_location",
    payloadKey: "location",
    label: "Search photos by state",
    placeholder: "Start typing your state...",
    notFoundText: "No locations found...",
  },
  username: {
    paramKey: "search_username",
    routeBase: "/find-username",
    searchEndpoint: "/find-username",
    photosEndpoint: "/photos/by_username",
    payloadKey: "username",
    label: "Search photo by username",
    placeholder: "Username...",
    notFoundText: "No usernames found...",
  },
};

const PhotoSearch = ({ searchType = "location" }) => {
  const config = SEARCH_CONFIG[searchType];
  const navigate = useNavigate();
  const params = useParams();
  
  // Extract the specific URL parameter based on the current configuration
  const urlParamValue = params[config.paramKey];

  const [query, setQuery] = useState(urlParamValue || "");
  const [suggestions, setSuggestions] = useState([]);
  const [photosToShow, setPhotosToShow] = useState(undefined);

  // Fetch suggestions from backend
  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}${config.searchEndpoint}`, {
        [config.payloadKey]: query,
      });

      const searchTerm = query.toLowerCase();

      // Separate exact matches and partial matches
      const exactMatches = response.data.filter((item) => item.toLowerCase() === searchTerm);
      const partialMatches = response.data.filter((item) => item.toLowerCase() !== searchTerm);

      setSuggestions([...exactMatches, ...partialMatches]);
    } catch (error) {
      console.error(`Error fetching ${searchType} suggestions:`, error);
    }
  }, [query, config, searchType]);

  // Fetch actual photos once a valid suggestion/query is finalized
  const handleFetchPhotos = useCallback(async (searchString) => {
    if (!searchString) return;
    setQuery(searchString);

    try {
      const response = await axios.post(`${BACKEND_URL}${config.photosEndpoint}`, {
        [config.payloadKey]: searchString,
      });
      setPhotosToShow(response.data);
    } catch (error) {
      console.error(`Error fetching photos for ${searchType}:`, error);
    }
  }, [config, searchType]);

  const handleSelectSuggestion = (selectedItem) => {
    if (selectedItem !== urlParamValue) {
      navigate(`${config.routeBase}/${selectedItem}`);
    } else {
      handleFetchPhotos(selectedItem);
    }
  };

  const onKeyUp = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  // Sync with URL parameter changes
  useEffect(() => {
    if (urlParamValue) {
      handleFetchPhotos(urlParamValue);
    }
  }, [urlParamValue, handleFetchPhotos]);

  // Debounce hook: triggers search 400ms after the user stops typing
  useEffect(() => {
    if (!query) {
      setSuggestions([]);
      return;
    }

    if (photosToShow) return; // Skip auto-search if photos are already displayed

    const delayDebounceFn = setTimeout(() => {
      handleSearch();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [query, photosToShow, handleSearch]);

  return (
    <div className="content-block gallery bg-light p-3 rounded">
      <Container
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            display: "flex",
            alignItems: "center",
            mb: 0,
          }}
        >
          <InputGroup className="search-box">
            <FloatingLabel label={config.label}>
              <Form.Control
                className="search-input"
                placeholder={config.placeholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPhotosToShow(undefined); // Clear photo view on new input
                }}
                onKeyUp={onKeyUp}
              />
            </FloatingLabel>
            <Button className="search-button" variant="success" onClick={handleSearch}>
              <SearchIcon />
            </Button>
          </InputGroup>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            display: "flex",
            alignItems: "center",
          }}
        >
          {!photosToShow && query.length > 0 && (
            <div className="search-list">
              <ListGroup>
                {suggestions.length > 0 ? (
                  suggestions.map((item) => (
                    <ListGroup.Item
                      className="search-item"
                      action
                      variant="outline-success"
                      key={item}
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      {item}
                    </ListGroup.Item>
                  ))
                ) : (
                  <ListGroup.Item className="search-item text-muted">
                    {config.notFoundText}
                  </ListGroup.Item>
                )}
              </ListGroup>
            </div>
          )}
        </Box>
      </Container>

      {photosToShow && <PhotoViewer photosToShow={photosToShow} />}
    </div>
  );
};

export default PhotoSearch;